import { useMemo, useState } from 'react';
import type { Contact, ContactAddress } from 'src/api/generated';
import { useFunnelStatuses } from '../../../../shared/hooks/api/crm/funnels/funnel-statuses';
import { useGlobalCallProviderContext } from 'src/contexts/global-call-provider-context';
import { useUpdateFunnelConversation } from '../../../hooks/api/funnels/funnel-conversations-mutations';
import { useTasks } from 'src/modules/shared/hooks/api/tasks/tasks';
import { useAllPersonAndSystemUsers } from 'src/modules/communications/hooks/users/system-users';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';
import { COMMUNICATIONS_SMALL_SCREEN_MEDIA_QUERY } from 'src/modules/communications/utils/view-preference.util';
import { ConversationTasksModal } from './conversation-tasks-modal';
import { ConversationHeaderCompact } from './conversation-header-compact';
import { ConversationHeaderMetadata } from './conversation-header-metadata';
import { ConversationHeaderSkeleton } from './conversation-header-skeleton';
import {
  getConversationName,
  getConversationStatusQuery,
} from 'src/modules/communications/utils/conversation-detail.util';
import {
  isConversationActiveDuplicate,
  isConversationOperationConflict,
} from 'src/modules/communications/utils/conversation-operation-conflict.util';

interface ConversationHeaderProps {
  isLoading: boolean;
  contact?: Contact;
  addresses?: ContactAddress[];
  funnelId?: number;
  funnelStatusId?: number;
  userId?: number;
  conversationId: number;
  aiStatusIds?: number[];
  isTestConversation?: boolean;
  onOpenProfile: (contactId: number) => void;
  onClose: () => void;
  showDesktopMetadata?: boolean;
}

export const ConversationHeader: React.FC<ConversationHeaderProps> = ({
  isLoading,
  contact,
  addresses,
  funnelId,
  funnelStatusId,
  userId,
  conversationId,
  aiStatusIds = [],
  isTestConversation = false,
  onOpenProfile,
  onClose,
  showDesktopMetadata = true,
}) => {
  const { t } = useI18n();
  const isSmallScreen = useMediaQuery(COMMUNICATIONS_SMALL_SCREEN_MEDIA_QUERY);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const { provider: callProvider, loading: providerLoading } =
    useGlobalCallProviderContext();

  const statusQuery = useMemo(
    () => getConversationStatusQuery(funnelId),
    [funnelId]
  );

  const { statuses, loading: loadingStatuses } = useFunnelStatuses(
    statusQuery,
    !!funnelId
  );

  const { users, loading: loadingAgents } = useAllPersonAndSystemUsers();

  const {
    requestUpdateConversation: updateConversation,
    loading: updatingConversation,
  } = useUpdateFunnelConversation();

  const { tasks: conversationTasks } = useTasks({
    conversationId,
    enabled: !!conversationId,
  });

  const conversationName = useMemo(
    () =>
      getConversationName({
        addresses,
        contact,
        isLoading,
      }),
    [addresses, contact, isLoading]
  );

  const handleStatusChange = async (statusId: number | null) => {
    try {
      await updateConversation({
        id: conversationId,
        data: { funnelStatusId: statusId },
      });
    } catch (error) {
      if (
        !isConversationActiveDuplicate(error) &&
        !isConversationOperationConflict(error)
      ) {
        throw error;
      }
    }
  };

  const handleUserAssign = async (nextUserId: number | null) => {
    await updateConversation({
      id: conversationId,
      data: { userId: nextUserId },
    });
  };

  const handleCall = (phoneNumber: string) => {
    if (providerLoading || !callProvider) return;

    void callProvider.callActions.startCall({ phoneNumber });
  };

  const handleOpenTaskModal = () => setIsTaskModalOpen(true);
  const handleCloseTaskModal = () => {
    setIsTaskModalOpen(false);
  };

  const shouldShowMetadata = isSmallScreen || showDesktopMetadata;

  const headerContent = shouldShowMetadata ? (
    <ConversationHeaderMetadata
      addresses={addresses}
      aiStatusIds={aiStatusIds}
      contactId={contact?.id}
      conversationName={conversationName}
      funnelStatusId={funnelStatusId}
      handleCall={handleCall}
      handleOpenTaskModal={handleOpenTaskModal}
      handleStatusChange={handleStatusChange}
      handleUserAssign={handleUserAssign}
      isLoading={isLoading}
      isSmallScreen={isSmallScreen}
      isTestConversation={isTestConversation}
      loadingAgents={loadingAgents}
      loadingStatuses={loadingStatuses}
      onClose={onClose}
      onOpenProfile={onOpenProfile}
      statuses={statuses}
      tasks={conversationTasks}
      updatingConversation={updatingConversation}
      userId={userId}
      users={users}
    />
  ) : (
    <ConversationHeaderCompact
      addresses={addresses}
      contactId={contact?.id}
      conversationName={conversationName}
      handleCall={handleCall}
      isLoading={isLoading}
      isTestConversation={isTestConversation}
      onClose={onClose}
      onOpenProfile={onOpenProfile}
      overflowItems={[
        {
          content: t('communications.conversationHeader.viewContactProfile'),
          onClick: () => onOpenProfile(contact?.id ?? 0),
          disabled: !contact?.id,
        },
        {
          content: t(
            'communications.contactPanel.conversations.fields.viewTasks'
          ),
          onClick: handleOpenTaskModal,
        },
      ]}
      t={t}
    />
  );

  return (
    <div>
      <div
        className={`border-b border-gray-200 bg-white px-3 sm:px-4 ${
          shouldShowMetadata ? 'min-h-[92px] py-2.5' : 'min-h-[72px] py-2'
        }`}
      >
        {isLoading ? (
          <ConversationHeaderSkeleton
            showMetadata={shouldShowMetadata}
            isSmallScreen={isSmallScreen}
          />
        ) : (
          headerContent
        )}
      </div>

      <ConversationTasksModal
        isOpen={isTaskModalOpen}
        onClose={handleCloseTaskModal}
        tasks={conversationTasks}
        conversationId={conversationId}
        subtitle={conversationName}
      />
    </div>
  );
};
